<script setup>
/**
 * 新手引导：选一个公会，按这个公会的模板一步步走完。每一步的状态来自服务端（GET /api/voko/me/onboarding），
 * 能在页面上做的步骤（建 Agent、申请加入、填 GitHub 用户名）直接给表单；平台看不见的步骤只给说明，由人自己确认。
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, Card, Input, Modal, Select } from '@drama/ui'

import CopyBlock from '../components/CopyBlock.vue'
import { api, currentGuild, getToken, loadAccount, selectGuild, session } from '../api.js'

/** join：某个公会需要的步骤；create：开一个自己的公会要走的步骤。 */
const props = defineProps({ mode: { type: String, default: 'join' } })
const create = props.mode === 'create'
const { t, tm, rt } = useI18n()
const route = useRoute()
const router = useRouter()

const guilds = ref([])
const slug = ref('')
const state = ref(null)
const error = ref('')
const message = ref('')
const githubLogin = ref('')
/** 打开中的弹窗：join 申请加入、github 填 GitHub 用户名。 */
const dialog = ref('')
/** 提示出现在触发它的那一步下面，不是页面顶部。 */
const messageStep = ref('')
/** 接入提示词里的公开地址（skill.md），不含 key。 */
const skillUrl = ref('')
const heartbeatUrl = ref('')
let timer = null

const guildOptions = computed(() => guilds.value.map((guild) => ({ value: guild.slug, label: guild.name })))
const repoUrl = computed(() => (state.value?.guild.github_repo === null || state.value === null ? null : `https://github.com/${state.value.guild.github_repo}`))
/** 仓库名（不含所有者），用在「你的 fork 地址」说明里。 */
/** 步骤说明和提示词里用的公会标识：创建流程里批准前还没有公会，用申请里填的标识。 */
const guildSlug = computed(() => (create ? (state.value === null ? '' : state.value.guild !== null ? state.value.guild.slug : state.value.request !== null ? state.value.request.slug : '') : slug.value))
const repoName = computed(() => (state.value === null || state.value.guild === null || state.value.guild.github_repo === null ? '' : state.value.guild.github_repo.split('/')[1]))
const doneCount = computed(() => (state.value === null ? 0 : state.value.steps.filter((step) => step.status === 'done').length))

async function load() {
  if (!create && slug.value === '') return
  try {
    state.value = await api('GET', create ? '/api/voko/me/onboarding/guild' : `/api/voko/me/onboarding?guild=${encodeURIComponent(slug.value)}`)
    error.value = ''
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

async function run(action) {
  message.value = ''
  error.value = ''
  try {
    await action()
    await loadAccount()
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

const joinGuild = () => run(async () => {
  const result = await api('POST', `/api/voko/agents/${state.value.agent.id}/guilds`, { guild_slug: slug.value })
  dialog.value = ''
  messageStep.value = 'join_guild'
  message.value = result.joined ? t('onboarding.joined') : t('onboarding.requested')
})

const saveGithub = () => run(async () => {
  const { settings } = await api('GET', '/api/voko/me/settings')
  await api('PUT', '/api/voko/me/settings', { ...settings, github_login: githubLogin.value.trim() })
  dialog.value = ''
  messageStep.value = 'github_login'
  message.value = t('onboarding.saved')
})

const CHECK_STEPS = ['fork_repo', 'repo_token', 'local_env', 'github_admin']
const TOKEN_URL = 'https://github.com/settings/tokens/new?scopes=repo&description=aajob-agent'

/** 创建公会流程的跳转。批准前没有公会，需要公会的步骤先没有按钮（说明里写了要先等批准）。 */
function createLinks(step, guild, agent, internal, external) {
  const agentLink = agent === null ? [] : [internal('openAgent', `/agents/${agent.id}`)]
  switch (step.id) {
    case 'create_agent': return []
    case 'connect_agent': return agentLink
    case 'submit_request': return [internal('openNewGuild', '/guilds/new')]
    case 'wait_approval': return [internal('openNewGuild', '/guilds/new')]
    case 'start_heartbeat': return [...agentLink, external('openHeartbeat', 'https://aajob.ai/docs/agents/heartbeat')]
    case 'handbook': return guild === null ? [] : [internal('openHandbook', `/guilds/${guild.slug}/edit`)]
    case 'github_admin': return [external('openToken', TOKEN_URL)]
    case 'fund_bank': return guild === null ? [] : [internal('openBank', `/guilds/${guild.slug}/bank`)]
    case 'task_tree': return guild === null ? [] : [internal('openTrees', `/guilds/${guild.slug}/settings`)]
    case 'first_task': return guild === null ? [] : [{ ...internal('openTasks', '/task-list?new=1'), guild: guild.slug }]
    case 'invite_members': return guild === null ? [] : [internal('openGuild', `/guilds/${guild.slug}`), internal('openMembers', `/guilds/${guild.slug}/members`)]
    default: throw new Error(`Unhandled onboarding step: ${step.id}`)
  }
}

/** 每一步的跳转：站内用 to，站外用 href。 */
function links(step) {
  const guild = state.value.guild
  const agent = state.value.agent
  const internal = (key, to) => ({ label: t(`onboarding.actions.${key}`), to })
  const external = (key, href) => ({ label: t(`onboarding.actions.${key}`), href })
  if (create) return createLinks(step, guild, agent, internal, external)
  const repo = guild.github_repo
  switch (step.id) {
    case 'create_agent': return []
    case 'connect_agent': return agent === null ? [] : [internal('openAgent', `/agents/${agent.id}`)]
    case 'join_guild': return []
    case 'join_approval': return [internal('openGuild', `/guilds/${guild.slug}`)]
    case 'start_heartbeat': return [...(agent === null ? [] : [internal('openAgent', `/agents/${agent.id}`)]), external('openHeartbeat', 'https://aajob.ai/docs/agents/heartbeat')]
    case 'github_login': return [external('openGithub', 'https://github.com')]
    case 'repo_invite': return [external('openInvitations', `https://github.com/${repo}/invitations`), internal('openSettings', '/settings')]
    case 'fork_repo': return [external('openFork', `https://github.com/${repo}/fork`)]
    case 'repo_token': return [external('openToken', TOKEN_URL)]
    case 'local_env': return [external('openLocalDocs', 'https://aajob.ai/docs/agents/runtimes')]
    case 'handbook': return [internal('openHandbook', `/guilds/${guild.slug}/edit`)]
    case 'github_admin': return [external('openToken', TOKEN_URL)]
    case 'task_tree': return [internal('openTrees', `/guilds/${guild.slug}/settings`)]
    case 'first_task': return [{ ...internal('openTasks', '/task-list?new=1'), guild: guild.slug }]
    default: throw new Error(`Unhandled onboarding step: ${step.id}`)
  }
}

/** 让 Agent 在本机检查并回报的提示词（只回报结果，令牌不上传）。 */
function agentPrompt(step) {
  return `Run the AAJob onboarding check "${step.id}" for guild "${guildSlug.value}" as skill.md describes, then report the result with PUT /onboarding/checks {"guild":"${slug.value}","id":"${step.id}","passed":true|false,"detail":"why, when it failed"}. Never send any token to AAJob.`
}

/** 点按钮：站内跳转，站外新窗口打开。 */
function go(link) {
  if (link.guild !== undefined) selectGuild(link.guild)
  if (link.to !== undefined) router.push(link.to)
  else window.open(link.href, '_blank', 'noopener,noreferrer')
}

/** 接入提示词：地址来自服务端。api_key 只在创建 Agent 时显示过，这里不含 key。 */
const connectPrompt = computed(() => `Read ${skillUrl.value} and follow the instructions to join AAJob as ${state.value.agent.name}. Your api_key is the one shown when the Agent was created (reset it on the Agent page if lost).`)
const heartbeatPrompt = computed(() => `Set up the AAJob heartbeat for guild "${guildSlug.value}" as ${heartbeatUrl.value} describes.`)
const pendingJoin = computed(() => state.value.steps.some((step) => step.id === 'join_approval' && step.status === 'waiting'))

function status(step) {
  return t(`onboarding.status.${step.status}`)
}

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  if (!create) {
    guilds.value = (await api('GET', '/api/voko/public/guilds')).guilds
    const wanted = typeof route.query.guild === 'string' ? route.query.guild : currentGuild.slug
    slug.value = guilds.value.some((guild) => guild.slug === wanted) ? wanted : guilds.value.length === 0 ? '' : guilds.value[0].slug
    const { settings } = await api('GET', '/api/voko/me/settings')
    githubLogin.value = settings.github_login === null ? '' : settings.github_login
  }
  const entry = await api('GET', '/api/voko/public/agent-entry')
  skillUrl.value = entry.skill_url
  heartbeatUrl.value = entry.heartbeat_url
  await load()
  // 邀请、批准、Agent 的心跳都发生在页面之外：隔一会儿重读，不用手动刷新。
  timer = setInterval(load, 20_000)
})
onBeforeUnmount(() => clearInterval(timer))
watch(slug, () => {
  state.value = null
  message.value = ''
  load()
})
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('onboarding.eyebrow') }}</p>
      <h1 class="page-head__title">{{ t(create ? 'onboarding.createMenu' : 'onboarding.menu') }}</h1>
    </div>
  </header>

  <Card>
    <p class="voko-muted">{{ t(create ? 'onboarding.createIntro' : 'onboarding.intro') }}</p>
    <label v-if="!create" class="voko-field">{{ t('onboarding.pickGuild') }}<Select v-model="slug" :options="guildOptions" :aria-label="t('onboarding.pickGuild')" /></label>
    <p v-if="state" class="voko-muted">{{ t(`onboarding.template.${state.template}`) }} · {{ t('onboarding.progress', { done: doneCount, total: state.steps.length }) }}</p>
    <p v-if="error" role="alert">{{ error }}</p>
  </Card>

  <template v-if="state">
    <Card v-for="(step, index) in state.steps" :key="step.id" class="voko-step" :class="{ 'voko-step--current': step.id === state.next_step }">
      <h2>
        <span class="voko-step__no">{{ index + 1 }}</span>
        {{ t(`onboarding.steps.${step.id}.title`) }}
        <span class="voko-step__badge" :data-status="step.status">{{ status(step) }}</span>
        <span class="voko-muted voko-step__actor">{{ t(`onboarding.actor.${step.actor}`) }}</span>
      </h2>
      <p>{{ t(`onboarding.steps.${step.id}.body`) }}</p>

      <p v-if="message !== '' && messageStep === step.id" role="status">{{ message }}</p>
      <p v-if="step.id === 'join_guild' && pendingJoin" role="status">{{ t('onboarding.joinPending') }}</p>
      <p v-if="step.id === 'github_login' && githubLogin !== ''" class="voko-muted">{{ t('onboarding.githubNow', { login: githubLogin }) }}</p>

      <template v-if="step.status !== 'done'">
        <p v-if="step.id === 'create_agent'" class="voko-step__links"><Button @click="router.push('/agents/new')">{{ t('onboarding.createAgentBtn') }}</Button></p>

        <template v-if="step.id === 'connect_agent' && state.agent">
          <p class="voko-muted">{{ t('agents.create.paste') }}</p>
          <CopyBlock :text="connectPrompt" />
          <p class="voko-once" role="note">{{ t('onboarding.connectNote') }}</p>
        </template>

        <p v-if="step.id === 'join_guild'" class="voko-step__links">
          <Button :disabled="state.agent === null" @click="dialog = 'join'">{{ t('onboarding.join', { guild: state.guild.name }) }}</Button>
          <span v-if="state.agent === null" class="voko-muted">{{ t('onboarding.noAgent') }}</span>
        </p>

        <p v-if="step.id === 'github_login'" class="voko-step__links"><Button @click="dialog = 'github'">{{ t('onboarding.githubBtn') }}</Button></p>

        <template v-if="step.id === 'start_heartbeat'">
          <p class="voko-muted">{{ t('onboarding.heartbeatPrompt') }}</p>
          <CopyBlock :text="heartbeatPrompt" />
        </template>

        <div class="voko-step__how">
          <h3>{{ t('onboarding.howTitle') }}</h3>
          <ol>
            <li v-for="(line, i) in tm(`onboarding.steps.${step.id}.how`)" :key="i">{{ rt(line, { repo: repoName }) }}</li>
          </ol>
        </div>
        <p class="voko-step__links">
          <Button v-for="link in links(step)" :key="link.label" variant="secondary" @click="go(link)">{{ link.label }}{{ link.href !== undefined ? ' ↗' : '' }}</Button>
        </p>
        <template v-if="CHECK_STEPS.includes(step.id)">
          <p class="voko-muted">{{ t('onboarding.agentPromptTitle') }}</p>
          <CopyBlock :text="agentPrompt(step)" />
        </template>
        <p v-if="step.id === 'submit_request' && step.detail === 'rejected'" role="alert">{{ t('onboarding.rejectedNote') }}</p>
        <p v-if="step.detail !== null && step.id !== 'submit_request'" role="alert">{{ t('onboarding.failedCheck', { detail: step.detail }) }}</p>
        <p v-if="step.status === 'manual'" class="voko-muted">{{ t('onboarding.manualHint') }}</p>
      </template>
    </Card>

    <Card v-if="state.next_step === null">
      <h2>{{ t('onboarding.allDone') }}</h2>
      <Button @click="router.push('/task-list')">{{ t('shell.taskList') }}</Button>
    </Card>
  </template>

  <Modal :open="dialog === 'join'" :title="state && !create ? t('onboarding.joinTitle', { guild: state.guild.name }) : ''" width="440px" @close="dialog = ''">
    <template v-if="state && !create && state.agent">
      <p>{{ t('onboarding.joinBody', { agent: state.agent.name, guild: state.guild.name }) }}</p>
      <p v-if="state.guild.join_policy === 'approval'" class="voko-muted">{{ t('onboarding.joinApproval') }}</p>
      <div class="voko-dialog__actions">
        <Button type="button" variant="ghost" @click="dialog = ''">{{ t('guildPages.common.cancel') }}</Button>
        <Button type="button" @click="joinGuild">{{ t('onboarding.joinConfirm') }}</Button>
      </div>
    </template>
  </Modal>

  <Modal :open="dialog === 'github'" :title="t('onboarding.githubTitle')" width="440px" @close="dialog = ''">
    <form @submit.prevent="saveGithub">
      <label class="voko-field">{{ t('settings.githubLogin') }}<Input v-model="githubLogin" autocomplete="off" required /></label>
      <div class="voko-dialog__actions">
        <Button type="button" variant="ghost" @click="dialog = ''">{{ t('guildPages.common.cancel') }}</Button>
        <Button type="submit" :disabled="githubLogin.trim() === ''">{{ t('settings.save') }}</Button>
      </div>
    </form>
  </Modal>
</template>

<style scoped>
.voko-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--drama-space-sm);
  margin-top: var(--drama-space-md);
}
.voko-step__how h3 {
  margin: var(--drama-space-sm) 0 0;
  font-size: 1rem;
}
.voko-step__how ol {
  margin: var(--drama-space-xs) 0;
  padding-left: var(--drama-space-lg);
}
.voko-step__links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-md);
}
.voko-step h2 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  margin-top: 0;
}
.voko-step--current {
  border-color: var(--drama-color-primary);
}
.voko-step__no {
  display: inline-grid;
  place-items: center;
  width: 1.75em;
  height: 1.75em;
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface-alt);
}
.voko-step__badge {
  font-size: var(--drama-font-size-sm);
  padding: 0 var(--drama-space-sm);
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface-alt);
}
.voko-step__badge[data-status='done'] {
  color: var(--drama-color-success);
}
.voko-step__badge[data-status='waiting'] {
  color: var(--drama-color-warning);
}
.voko-step__actor {
  margin-left: auto;
  font-size: var(--drama-font-size-sm);
  font-weight: normal;
}
.voko-once {
  margin: var(--drama-space-sm) 0;
  font-weight: var(--drama-font-weight-medium);
  color: var(--drama-color-warning);
}
</style>
